import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PatientBasicDetails from './PatientBasicDetails';
import PrescriptionSection from './PrescriptionSection';
import FollowUpSection from './FollowUpSection';
import Diagnosis from './Diagnosis';
import ConsultationLabReports from './ConsultationLabReports';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ViewDiagnosisRecordButton from './viewDiagnosisRecord';
import { AppointmentStatus, Slot, VisitType, SurgicalStatus } from '@/types/types';
import { CheckCircle, ArrowLeft, Clock, FileText, Stethoscope, Calendar, Save, Activity, FlaskConical, MessageCircle, Pencil } from 'lucide-react';
import { doctorApi } from '@/api/doctor';
import { appointmentApi } from '@/api/appointment';
import { notificationApi } from '@/api/notification';
import { diagnosisApi } from '@/api/diagnosis';
import { dayCareApi } from '@/api/daycare';
import type { DayCareProcedure, DayCareResource } from '@/types/daycare';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { isDiagnosisWithinEditWindow, getDiagnosisEditableUntil } from '@/utils/diagnosisEditWindow';
import { format } from 'date-fns';


interface DiagnosisFormData {
    diagnosis: string;
    notes: string | null;
    medicines: { name: string; frequency: string; duration: string; drugId?: string; notes?: string }[];
    labTests: Array<{ id?: string; name?: string }>;
    appointmentId: string;
    followUpAppointmentId?: string;
}

interface PrescriptionData {
    medicines: { name: string; frequency: string; duration: string; drugId?: string; notes?: string }[];
    tests: { name: string; id: string; instructions?: string }[];
    clinicalNotes: { note: string; category?: string }[];
    pendingClinicalNote?: string;
}

type FollowUpData = {
    surgicalStatus: 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED';
    followUpAppointmentId?: string;
    followUpDateTime?: string;
    surgicalCategory?: string;
    surgicalDescription?: string;
    surgeryDate?: string;
};

const initialPrescriptionData: PrescriptionData = {
    medicines: [],
    tests: [],
    clinicalNotes: [],
    pendingClinicalNote: '',
};

const initialFollowUpData: FollowUpData = {
    surgicalStatus: 'NOT_REQUIRED',
    followUpAppointmentId: undefined,
    followUpDateTime: undefined,
    surgicalCategory: undefined,
    surgicalDescription: undefined,
    surgeryDate: undefined
};

function ConsultationPage() {
    const { patientId, appointmentId } = useParams() as { patientId: string; appointmentId: string };
    const [searchParams] = useSearchParams();
    const wantsEdit = searchParams.get('edit') === '1';
    const { user } = useAuth();
    const doctorId = user?.id || '';
    const doctorDept = user?.specialisation || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // State management
    const [diagnosisText, setDiagnosisText] = useState('');
    const [notesText] = useState('');
    const [prescriptionData, setPrescriptionData] = useState<PrescriptionData>(initialPrescriptionData);
    const [followUpData, setFollowUpData] = useState<FollowUpData>(initialFollowUpData);
    const [surgicalStatus, setSurgicalStatus] = useState<'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED'>('NOT_REQUIRED');
    const [isDayCareDialogOpen, setIsDayCareDialogOpen] = useState(false);
    const [dayCareProcedures, setDayCareProcedures] = useState<DayCareProcedure[]>([]);
    const [dayCareResources, setDayCareResources] = useState<DayCareResource[]>([]);
    const [selectedProcedureId, setSelectedProcedureId] = useState<string>('');
    const [selectedResourceId, setSelectedResourceId] = useState<string>('');
    const [dayCareNotes, setDayCareNotes] = useState<string>('');
    const [dayCareLoading, setDayCareLoading] = useState(false);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
    const [labTestsSent, setLabTestsSent] = useState(false);
    const [consultationSaved, setConsultationSaved] = useState(false);
    const [clearTestsSignal, setClearTestsSignal] = useState(0);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFormReady, setEditFormReady] = useState(false);
    const [initialPrescriptionSeed, setInitialPrescriptionSeed] = useState<{
        medicines: PrescriptionData['medicines'];
        clinicalNotes: { note: string }[];
    } | null>(null);

    const orderLabTestsMutation = useMutation({
        mutationFn: async (labTests: Array<{ id?: string; name?: string }>) => {
            return diagnosisApi.orderLabTestsForBilling(appointmentId, labTests);
        },
        onSuccess: async () => {
            setPrescriptionData((prev) => ({ ...prev, tests: [] }));
            setClearTestsSignal((n) => n + 1);
            setLabTestsSent(true);
            await queryClient.invalidateQueries({ queryKey: ['appointment-lab-tests', appointmentId] });
            toast.success('Lab tests sent for billing — send patient to reception');
            setTimeout(() => {
                document.getElementById('consultation-lab-reports')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to order lab tests');
        },
    });

    const handleSendForBilling = useCallback(() => {
        if (!prescriptionData.tests.length) {
            toast.error('Add at least one lab test first');
            return;
        }
        const labTests = prescriptionData.tests.map((test) =>
            test.id && !test.id.startsWith('custom-')
                ? { id: test.id }
                : { name: test.name }
        );
        orderLabTestsMutation.mutate(labTests);
    }, [prescriptionData.tests, orderLabTestsMutation]);

    const handleSendWhatsApp = useCallback(async () => {
        setSendingWhatsApp(true);
        try {
            await notificationApi.sendPrescriptionByAppointment(appointmentId);
            toast.success('Prescription sent on WhatsApp');
        } catch (error: any) {
            const msg = error.response?.data?.message || '';
            if (msg.includes('No prescription found')) {
                try {
                    await notificationApi.sendDiagnosisRecord(appointmentId);
                    toast.success('Consultation report sent on WhatsApp');
                } catch (inner: any) {
                    toast.error(inner.response?.data?.message || 'Failed to send on WhatsApp');
                }
            } else {
                toast.error(msg || 'Failed to send on WhatsApp');
            }
        } finally {
            setSendingWhatsApp(false);
        }
    }, [appointmentId]);

    const updateAppointmentStatus = useCallback(async () => {
        try {
            await api.patch(`/api/appointment/update-status/${appointmentId}`, {
                status: AppointmentStatus.DIAGNOSED
            });
            toast.success('Appointment marked as diagnosed');
        } catch (error: any) {
            console.error('Error updating appointment status:', error);
            toast.error(error.response?.data?.message || 'Failed to update appointment status');
            throw error;
        }
    }, [appointmentId]);

    const createFollowUpAppointment = useCallback(async () => {
        if (!followUpData.followUpDateTime) return null;

        try {
            // First check for existing slots at this time
            const appointmentDate = new Date(followUpData.followUpDateTime);
            const startDate = new Date(appointmentDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(appointmentDate);
            endDate.setHours(23, 59, 59, 999);

            const slotsResponse = await doctorApi.getSlots(doctorId, {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            });

            // Find slot that matches the exact time
            const existingSlot = slotsResponse.find((slot: Slot) => {
                const slotTime = new Date(slot.timeSlot);
                return slotTime.getTime() === appointmentDate.getTime();
            });

            // Create the appointment first
            const response = await appointmentApi.bookAppointment({
                patientId,
                doctorId,
                visitType: VisitType.FOLLOW_UP,
                scheduledAt: new Date(followUpData.followUpDateTime),
                status: AppointmentStatus.PENDING
            });

            const appointmentId = response.data.data.id;

            // Handle slot booking logic same as AddAppointment
            if (existingSlot && existingSlot.appointment1Id && !existingSlot.appointment2Id) {
                // Slot is partially booked, update with second appointment
                await doctorApi.updateSlot(existingSlot.id, {
                    appointment2Id: appointmentId,
                    timeSlot: new Date(followUpData.followUpDateTime)
                });
            } else {
                // No existing slot or slot is full, create new slot
                await doctorApi.addSlot(doctorId, {
                    appointment1Id: appointmentId,
                    timeSlot: new Date(followUpData.followUpDateTime)
                });
            }

            toast.success('Follow-up appointment scheduled');
            return appointmentId;
        } catch (error: any) {
            console.error('Error creating follow-up appointment:', error);
            toast.error(error.response?.data?.message || 'Failed to schedule follow-up appointment');
            return null;
        }
    }, [patientId, doctorId, followUpData.followUpDateTime]);

    const createSurgeryAppointment = useCallback(async () => {
        if (!followUpData.surgicalCategory) {
            throw new Error('Surgery category is required');
        }

        const surgeryData = {
            appointmentId,
            category: followUpData.surgicalCategory,
            scheduledAt: followUpData.surgeryDate ? new Date(followUpData.surgeryDate) : undefined,
            status: followUpData.surgicalStatus as SurgicalStatus,
            description: followUpData.surgicalDescription || 'No description provided'
        };

        const response = await appointmentApi.addSurgery(surgeryData);

        // If we get here without an exception, the surgery was created successfully
        if (response.data) {
            toast.success('Surgery appointment scheduled');
            return response.data.id || response.data.data?.id;
        }

        throw new Error('Failed to schedule surgery');
    }, [appointmentId, followUpData]);

    const diagnosisMutation = useMutation({
        mutationFn: async (data: DiagnosisFormData) => {
            if (isEditMode) {
                return diagnosisApi.updateDiagnosis(appointmentId, {
                    diagnosis: data.diagnosis,
                    notes: data.notes,
                    medicines: data.medicines,
                    labTests: data.labTests,
                });
            }

            let followUpAppointmentId = null;

            if (followUpData.followUpDateTime) {
                followUpAppointmentId = await createFollowUpAppointment();
            }

            if (surgicalStatus === 'NOT_CONFIRMED' || surgicalStatus === 'CONFIRMED') {
                try {
                    await createSurgeryAppointment();
                } catch (error: any) {
                    console.error('Surgery creation failed:', error);
                    toast.error('Failed to create surgery appointment, but proceeding with diagnosis');
                }
            }

            // Update the data with the actual followUpAppointmentId
            const updatedData = {
                ...data,
                followUpAppointmentId: followUpAppointmentId
            };

            const diagnosisResponse = await api.post(`/api/diagnosis/add-diagnosis?appointmentId=${appointmentId}`, updatedData);

            // After creating diagnosis and lab test orders, add lab tests to bill
            return diagnosisResponse;
        },
        onSuccess: async () => {
            if (!isEditMode) {
                await updateAppointmentStatus();
            }
            await queryClient.invalidateQueries({ queryKey: ['diagnosis-record', appointmentId] });
            await queryClient.invalidateQueries({ queryKey: ['appointments'] });
            setConsultationSaved(true);
            toast.success(
                isEditMode
                    ? 'Diagnosis record updated'
                    : 'Diagnosis record saved — you can send on WhatsApp when ready'
            );
        },
        onError: (error: any) => {
            console.error('Error saving diagnosis record:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save diagnosis record';

            // Handle specific error cases
            if (error.response?.data?.message?.includes('Unique constraint failed')) {
                toast.error('Diagnosis record already exists for this appointment');
            } else if (error.response?.data?.message?.includes('Foreign key constraint')) {
                toast.error('Some lab tests are not available. Please check and try again.');
            } else {
                toast.error(errorMessage);
            }
        }
    });

    // Patient data query


    // Check if diagnosis already exists
    const getDiagnosis = async () => {
        try {
            const response = await api.get(`/api/diagnosis/get-by-appointment/${appointmentId}`);
            return response.data.data;
        } catch (error: any) {
            return null;
        }
    }
    const [existingDiagnosis, setExistingDiagnosis] = useState<any>(null);
    const [isDiagnosisLoading, setIsDiagnosisLoading] = useState(true);

    useEffect(() => {
        const fetchDiagnosis = async () => {
            setIsDiagnosisLoading(true);
            const diagnosis = await getDiagnosis();
            setExistingDiagnosis(diagnosis);
            setIsDiagnosisLoading(false);
        }
        fetchDiagnosis();
    }, [appointmentId]);

    const canEditExisting = useMemo(
        () =>
            !!existingDiagnosis &&
            (existingDiagnosis.isEditable === true ||
                isDiagnosisWithinEditWindow(existingDiagnosis.createdAt)),
        [existingDiagnosis]
    );

    useEffect(() => {
        if (!existingDiagnosis || editFormReady) return;
        if (!canEditExisting || !wantsEdit) return;

        const medicines = Array.isArray(existingDiagnosis.medicines)
            ? existingDiagnosis.medicines.map((m: any) => ({
                  name: m.name || '',
                  frequency: m.frequency || 'Once a day',
                  duration: m.duration || '',
                  drugId: m.drugId,
                  notes: m.notes,
              }))
            : [];
        const notes = typeof existingDiagnosis.notes === 'string' ? existingDiagnosis.notes : '';
        const clinicalNotes = notes.trim() ? [{ note: notes }] : [];

        setDiagnosisText(existingDiagnosis.diagnosis || '');
        setPrescriptionData({
            medicines,
            tests: [],
            clinicalNotes,
            pendingClinicalNote: '',
        });
        setInitialPrescriptionSeed({ medicines, clinicalNotes });
        setIsEditMode(true);
        setEditFormReady(true);
    }, [existingDiagnosis, wantsEdit, canEditExisting, editFormReady]);


    // Form validation
    const validateForm = useCallback(() => {
        if (!appointmentId) {
            toast.error('No appointment ID provided');
            return false;
        }
        if (!diagnosisText.trim()) {
            toast.error('Diagnosis is required');
            return false;
        }

        else if (!diagnosisText.trim() && !prescriptionData.medicines.length && !prescriptionData.tests.length) {
            toast.error('Fill in at least one diagnosis or prescription');
            return false;
        }

        return true;
    }, [appointmentId, diagnosisText, prescriptionData.medicines]);

    // Form submission
    const handleSubmit = useCallback(async() => {
        if (!validateForm()) return;

        // Combine added clinical notes + any text still in the note box (do not require "Add note").
        const combinedNotes = [
            ...prescriptionData.clinicalNotes.map((note) =>
                note.category ? `${note.category}: ${note.note}` : note.note
            ),
            prescriptionData.pendingClinicalNote?.trim() || '',
        ]
            .filter(Boolean)
            .join('\n');

        const diagnosisData: DiagnosisFormData = {
            diagnosis: diagnosisText,
            notes: combinedNotes || notesText || null,
            medicines: prescriptionData.medicines,
            labTests: prescriptionData.tests.map((test) =>
                test.id && !test.id.startsWith('custom-')
                    ? { id: test.id }
                    : { name: test.name }
            ),
            followUpAppointmentId: followUpData.followUpAppointmentId,
            appointmentId
        };

        diagnosisMutation.mutate(diagnosisData);
    }, [validateForm, diagnosisText, notesText, prescriptionData, followUpData.followUpAppointmentId, appointmentId, diagnosisMutation]);

    const handleGoBack = useCallback(() => {
        navigate('/doctor/appointments');
    }, [navigate]);

    const handleFollowUpChange = useCallback((data: FollowUpData) => {
        setSurgicalStatus(data.surgicalStatus);
        setFollowUpData(data);
    }, []);

    const openDayCareDialog = useCallback(async () => {
        try {
            setIsDayCareDialogOpen(true);
            setDayCareLoading(true);
            const [procRes, resRes] = await Promise.all([
                dayCareApi.listProcedures(),
                dayCareApi.listResources(),
            ]);
            setDayCareProcedures(procRes.data?.data ?? []);
            setDayCareResources(resRes.data?.data ?? []);
        } catch (error: any) {
            console.error('Failed to load Day Care masters', error);
            toast.error(error?.response?.data?.message || 'Failed to load Day Care procedures/resources');
        } finally {
            setDayCareLoading(false);
        }
    }, []);

    const handleCreateDayCareCase = useCallback(async () => {
        if (!patientId) {
            toast.error('Patient not found for Day Care case');
            return;
        }
        if (!selectedProcedureId) {
            toast.error('Please select a Day Care procedure');
            return;
        }
        try {
            setDayCareLoading(true);
            await dayCareApi.createCase({
                patientId,
                procedureId: selectedProcedureId,
                doctorId,
                resourceId: selectedResourceId || undefined,
                notes: dayCareNotes || undefined,
                scheduledAt: new Date().toISOString(),
            });
            toast.success('Day Care case created');
            setIsDayCareDialogOpen(false);
            setSelectedProcedureId('');
            setSelectedResourceId('');
            setDayCareNotes('');
        } catch (error: any) {
            console.error('Failed to create Day Care case', error);
            toast.error(error?.response?.data?.message || 'Failed to create Day Care case');
        } finally {
            setDayCareLoading(false);
        }
    }, [patientId, selectedProcedureId, selectedResourceId, dayCareNotes, doctorId]);



    // Check if diagnosis already exists and is outside the edit window
    if (isDiagnosisLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="h-5 w-5 animate-spin" />
                    Loading consultation…
                </div>
            </div>
        );
    }

    const showLockedGate =
        !!existingDiagnosis && !isEditMode && !canEditExisting;
    const showEditChoice =
        !!existingDiagnosis && !isEditMode && canEditExisting && !wantsEdit;
    const preparingEdit =
        !!existingDiagnosis && canEditExisting && wantsEdit && !isEditMode;

    if (preparingEdit) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="h-5 w-5 animate-spin" />
                    Opening diagnosis for edit…
                </div>
            </div>
        );
    }

    if (showLockedGate || showEditChoice) {
        const editableUntil = existingDiagnosis.editableUntil
            || (existingDiagnosis.createdAt
                ? getDiagnosisEditableUntil(existingDiagnosis.createdAt)
                : null);

        return (
            <div className="min-h-[60vh] bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full border border-blue-100">
                    <div className="mb-6">
                        <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-blue-800 mb-2">Diagnosis Already Exists</h2>
                        <p className="text-blue-600">
                            {canEditExisting
                                ? 'A diagnosis record exists for this appointment. You can still edit it within 48 hours of saving.'
                                : 'A diagnosis record has already been created for this appointment'}
                        </p>
                        {!canEditExisting && editableUntil && (
                            <p className="mt-2 text-sm text-slate-500">
                                Edit window closed on {format(new Date(editableUntil), 'PPp')}
                            </p>
                        )}
                    </div>

                    <div className="space-y-3">
                        {canEditExisting && (
                            <button
                                type="button"
                                onClick={() => {
                                    navigate(
                                        `/doctor/consultation/${patientId}/${appointmentId}?edit=1`,
                                        { replace: true }
                                    );
                                }}
                                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-800 transition-colors font-medium"
                            >
                                <Pencil className="w-4 h-4" />
                                Edit Diagnosis
                            </button>
                        )}
                        <button
                            onClick={handleGoBack}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Appointments
                        </button>
                        <ViewDiagnosisRecordButton appointmentId={appointmentId} />
                    </div>
                </div>
            </div>
        );
    }

    // Main consultation form
    return (
        <>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
            <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
                <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleGoBack}
                            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span className="hidden font-medium sm:inline">Back</span>
                        </button>
                        <div className="hidden h-6 w-px bg-slate-200 sm:block" />
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-600 to-teal-700 shadow-sm">
                                <Stethoscope className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                                    {isEditMode ? 'Edit Consultation' : 'Patient Consultation'}
                                </h1>
                                <p className="text-xs text-slate-500">
                                    {isEditMode
                                        ? 'Update diagnosis & prescription (within 48 hours)'
                                        : 'OPD diagnosis & prescription'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            className="hidden gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50 sm:flex"
                            onClick={openDayCareDialog}
                        >
                            <Activity className="h-4 w-4" />
                            Day Care
                        </Button>
                        <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs text-slate-600">
                            <Clock className="h-3.5 w-3.5" />
                            {new Date().toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            })}
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 sm:py-8">
                {patientId && (
                    <div className="mb-6">
                        <PatientBasicDetails patientId={patientId} />
                    </div>
                )}

                <div className="space-y-6">
                        {isEditMode && existingDiagnosis?.createdAt && !consultationSaved && (
                            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                                <Pencil className="h-5 w-5 shrink-0" />
                                <p className="text-sm font-medium">
                                    Editing saved diagnosis — changes allowed until{' '}
                                    {format(getDiagnosisEditableUntil(existingDiagnosis.createdAt), 'PPp')}
                                </p>
                            </div>
                        )}
                        {consultationSaved && (
                            <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                                <CheckCircle className="h-5 w-5 shrink-0" />
                                <p className="text-sm font-medium">
                                    {isEditMode
                                        ? 'Consultation updated. Send on WhatsApp or return to appointments.'
                                        : 'Consultation saved. Send the prescription on WhatsApp or return to appointments.'}
                                </p>
                            </div>
                        )}
                        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                            <div className="border-b border-slate-100 bg-gradient-to-r from-blue-50/80 to-white px-5 py-4 sm:px-6">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
                                        <FileText className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-900">Primary diagnosis</h2>
                                        <p className="text-sm text-slate-500">Condition, assessment, and clinical observations</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 sm:p-6">
                                <Diagnosis value={diagnosisText} onChange={setDiagnosisText} />
                            </div>
                        </section>

                        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                            <div className="border-b border-slate-100 bg-gradient-to-r from-teal-50/80 to-white px-5 py-4 sm:px-6">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-700 text-white shadow-sm">
                                            <Activity className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <h2 className="text-base font-semibold text-slate-900">Prescription & lab tests</h2>
                                            <p className="text-sm text-slate-500">
                                                Order labs first for billing, or add medicines and save together
                                            </p>
                                        </div>
                                    </div>
                                    {prescriptionData.tests.length > 0 && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleSendForBilling}
                                            disabled={orderLabTestsMutation.isPending}
                                            className="gap-1.5 border-blue-300 text-blue-800 hover:bg-blue-50"
                                        >
                                            <FlaskConical className="h-4 w-4" />
                                            {orderLabTestsMutation.isPending ? 'Sending…' : 'Send for billing'}
                                        </Button>
                                    )}
                                </div>
                                {labTestsSent && (
                                    <p className="mt-2 text-xs text-blue-700">
                                        Lab tests ordered — patient should pay at reception before tests proceed.
                                    </p>
                                )}
                            </div>
                            <div className="p-5 sm:p-6">
                                <PrescriptionSection
                                    onPrescriptionChange={setPrescriptionData}
                                    clearTestsSignal={clearTestsSignal}
                                    onSendLabTestsForBilling={handleSendForBilling}
                                    isSendingLabTests={orderLabTestsMutation.isPending}
                                    initialPrescription={initialPrescriptionSeed ?? undefined}
                                />
                            </div>
                        </section>

                        <div id="consultation-lab-reports">
                            <ConsultationLabReports appointmentId={appointmentId} />
                        </div>

                        {!isEditMode && (
                        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                            <div className="border-b border-slate-100 bg-gradient-to-r from-violet-50/80 to-white px-5 py-4 sm:px-6">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-600 text-white shadow-sm">
                                        <Calendar className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-900">Follow-up & surgery</h2>
                                        <p className="text-sm text-slate-500">Schedule return visits or surgical planning</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 sm:p-6">
                                <FollowUpSection
                                    doctorId={doctorId || ''}
                                    doctorDept={doctorDept || ''}
                                    onFollowUpChange={handleFollowUpChange}
                                />
                            </div>
                        </section>
                        )}

                        <div className="sticky bottom-4 z-10 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
                            <div>
                                <h3 className="font-semibold text-slate-900">
                                    {isEditMode ? 'Update consultation' : 'Complete consultation'}
                                </h3>
                                <p className="mt-0.5 text-sm text-slate-500">
                                    Diagnosis is required · review lab reports before saving if ordered
                                </p>
                            </div>
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                {prescriptionData.tests.length > 0 && !consultationSaved && (
                                    <Button
                                        type="button"
                                        onClick={handleSendForBilling}
                                        disabled={orderLabTestsMutation.isPending}
                                        size="lg"
                                        className="w-full gap-2 bg-blue-600 hover:bg-blue-700 sm:w-auto"
                                    >
                                        <FlaskConical className="h-4 w-4" />
                                        {orderLabTestsMutation.isPending ? 'Sending…' : 'Send lab tests for billing'}
                                    </Button>
                                )}
                                <Button
                                    onClick={handleSendWhatsApp}
                                    disabled={sendingWhatsApp || (!consultationSaved && !isEditMode)}
                                    variant="outline"
                                    size="lg"
                                    className="w-full gap-2 border-green-300 text-green-800 hover:bg-green-50 sm:w-auto"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    {sendingWhatsApp ? 'Sending…' : 'Send on WhatsApp'}
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={diagnosisMutation.isPending || consultationSaved}
                                    size="lg"
                                    className="w-full gap-2 bg-teal-700 hover:bg-teal-800 sm:w-auto"
                                >
                                    {diagnosisMutation.isPending ? (
                                        <>
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4" />
                                            {isEditMode ? 'Save changes' : 'Save consultation'}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
            </main>
        </div>
        <Dialog open={isDayCareDialogOpen} onOpenChange={setIsDayCareDialogOpen}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Send to Day Care</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Procedure</label>
                        <Select
                            value={selectedProcedureId}
                            onValueChange={setSelectedProcedureId}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={dayCareLoading ? 'Loading...' : 'Select a procedure'} />
                            </SelectTrigger>
                            <SelectContent>
                                {dayCareProcedures.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Resource (optional)</label>
                        <Select
                            value={selectedResourceId}
                            onValueChange={setSelectedResourceId}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select a resource (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="">None</SelectItem>
                                {dayCareResources.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        {r.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                        <Textarea
                            value={dayCareNotes}
                            onChange={(e) => setDayCareNotes(e.target.value)}
                            placeholder="Any special instructions for Day Care..."
                            rows={3}
                        />
                    </div>
                </div>
                <DialogFooter className="pt-4">
                    <Button variant="outline" onClick={() => setIsDayCareDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreateDayCareCase} disabled={dayCareLoading}>
                        {dayCareLoading ? 'Creating...' : 'Create Day Care Case'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}

export default ConsultationPage;
